// src/pages/orders/OrderDetail.tsx
//
// OrderDetail
// - Shows order base info, eSIM list, reservation items (if any), refund actions (admin only).
// - Uses StandardTableFrame for History (events) list.
// - Uses AppPageHeader slots to set a contextual leading title.
// - Memo events can be added/deleted (non-partner only).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DeleteOutline, Replay } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useRecoilValue } from 'recoil';

import OrderBaseTable from '@components/order/OrderBaseTable';
import EsimListTable from '@components/order/EsimListTable';
import { ReservationItemTable } from '@components/order/ReservationItemTable';
import DangerZoneCard from '@components/common/DangerZoneCard';
import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { useOrderDetail } from '@queries/orders/useOrderDetail';
import { useOrderEvents } from '@queries/orders/useOrderEvents';
import { useOrderReservationItems } from '@queries/orders/useOrderReservationItems';

import { addOrderMemo, deleteOrderMemo, type OrderEventResponse } from '@apis/order';
import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';
import { KOREAN_TIME_ZONE } from '@constants/timezone';

import { StandardTableFrame, cellSx } from '@components/common/table';

/**
 * Convert an API timestamp to a KST date-time string.
 */
const formatKstDateTime = (input: string | number) => {
  const utcDate = new Date(input);
  const kstDate = utcToZonedTime(utcDate, KOREAN_TIME_ZONE);
  return `${format(kstDate, 'yyyy-MM-dd HH:mm:ss')} KST`;
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const user = useRecoilValue(userState);
  const isPartner = user?.group === UserGroup.PARTNER;

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const safeOrderId = useMemo(() => {
    const n = Number(orderId);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [orderId]);

  const isValidOrderId = safeOrderId > 0;

  const [memoDraft, setMemoDraft] = useState('');
  const [fullRefundEsim, setFullRefundEsim] = useState(false);

  const { data: orderDetailData, refetch: refetchOrderDetail } = useOrderDetail(isValidOrderId ? safeOrderId : 0);

  const {
    data: orderEventsData,
    refetch: refetchOrderEvents,
    isLoading: isEventsLoading = false,
  } = useOrderEvents(isValidOrderId ? safeOrderId : 0);

  const { data: reservationItemsData, refetch: refetchReservationItems } = useOrderReservationItems(
    isValidOrderId ? safeOrderId : 0,
  );

  const orderDetail = orderDetailData?.data?.[0] ?? null;
  const eventList: OrderEventResponse[] = orderEventsData?.data ?? [];

  const reservationList = reservationItemsData?.data ?? [];
  const isReservationOrder = Array.isArray(reservationList) && reservationList.length > 0;

  const isRefundable = Boolean(orderDetail && orderDetail.paymentAmount > orderDetail.refundAmount);
  const canShowRefund = !isPartner && isRefundable;

  const headerLeading = useMemo(() => {
    if (!orderDetail) return undefined;
    return { title: `Order #${orderDetail.orderCode}` };
  }, [orderDetail]);

  // Header slots: set leading; clear on unmount to avoid leaking.
  useEffect(() => {
    if (!orderDetail) return;

    setPageHeaderSlots({ leading: headerLeading });
    return () => setPageHeaderSlots({});
  }, [headerLeading, orderDetail, setPageHeaderSlots]);

  const refreshAll = useCallback(async () => {
    try {
      await Promise.all([refetchOrderDetail(), refetchReservationItems(), refetchOrderEvents()]);
    } catch {
      // best-effort
    }
  }, [refetchOrderDetail, refetchOrderEvents, refetchReservationItems]);

  const onRefund = useCallback(() => {
    refreshAll();

    // Refresh list pages in the background.
    queryClient.invalidateQueries({ queryKey: ['orders'], exact: false });
  }, [queryClient, refreshAll]);

  const handleAddMemo = useCallback(async () => {
    if (!isValidOrderId) return;

    const value = memoDraft.trim();
    if (!value) return;

    try {
      await addOrderMemo(safeOrderId, value);
      systemAlert.success('Memo added.');
      setMemoDraft('');
      refetchOrderEvents();
    } catch {
      systemAlert.error('Failed to add memo.');
    }
  }, [isValidOrderId, memoDraft, refetchOrderEvents, safeOrderId, systemAlert]);

  const handleDeleteEvent = useCallback(
    async (orderEventId: number) => {
      if (!isValidOrderId) return;
      if (isPartner) return;

      const ok = await confirm({
        title: 'Delete this event?',
        description: 'This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        priority: 'danger',
      });

      if (!ok) return;

      try {
        await deleteOrderMemo(safeOrderId, orderEventId);
        systemAlert.success('Event deleted.');
        refetchOrderEvents();
      } catch {
        systemAlert.error('Failed to delete event.');
      }
    },
    [confirm, isPartner, isValidOrderId, refetchOrderEvents, safeOrderId, systemAlert],
  );

  if (!isValidOrderId) return null;
  if (!orderDetail) return null;

  return (
    <Box width="100%">
      <Box mt={{ xs: 2, md: 3 }} />

      <OrderBaseTable order={orderDetail} />

      <Box mt={{ xs: 2, md: 3 }} />

      {orderDetail.memo ? (
        <>
          <TextField
            label="Errors"
            value={orderDetail.memo}
            multiline
            minRows={6}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
          />
          <Box mt={{ xs: 2, md: 3 }} />
        </>
      ) : null}

      <EsimListTable
        isRefundable={isRefundable}
        order={orderDetail}
        fullRefund={fullRefundEsim}
        setFullRefund={setFullRefundEsim}
        onRefund={onRefund}
        hideList={isReservationOrder}
      />

      {isReservationOrder ? (
        <Box mt={{ xs: 2, md: 3 }}>
          <ReservationItemTable reservationItems={reservationList} orderId={orderDetail.orderId} />
        </Box>
      ) : null}

      {canShowRefund ? (
        <Box mt={{ xs: 3, md: 4 }}>
          <DangerZoneCard
            priority="danger"
            title="Full Refund"
            description="This will trigger the full refund flow for this order."
            actions={
              <Button
                variant="contained"
                color="error"
                startIcon={<Replay />}
                onClick={() => setFullRefundEsim(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                REFUND
              </Button>
            }
          />
        </Box>
      ) : null}

      <Box mt={{ xs: 3, md: 4 }} />

      {/* History */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              History
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              {eventList.length} event{eventList.length !== 1 ? 's' : ''} in total
            </Typography>
          }
        />

        <CardContent>
          <StandardTableFrame<OrderEventResponse>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date (KST)</TableCell>
                {!isPartner && <TableCell align="center">Delete</TableCell>}
              </TableRow>
            }
            rows={eventList}
            getRowKey={(e) => e.orderEventId}
            columnsCount={isPartner ? 5 : 6}
            isLoading={isEventsLoading}
            loadingVariant="skeleton"
            skeletonRows={6}
            emptyText="No events found."
            minWidth={900}
            renderRow={(event, index) => (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {index + 1}
                </TableCell>
                <TableCell sx={cellSx.nowrap}>{event.eventType}</TableCell>
                <TableCell sx={cellSx.nowrap}>{event.owner}</TableCell>
                <TableCell>{event.description}</TableCell>
                <TableCell sx={cellSx.nowrap}>{formatKstDateTime(event.createdAt)}</TableCell>

                {!isPartner && (
                  <TableCell align="center" sx={cellSx.nowrap}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEvent(event.orderEventId)}
                      aria-label="Delete event"
                      disabled={isEventsLoading}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </>
            )}
          />
        </CardContent>
      </Card>

      {/* Add Memo (internal) */}
      {!isPartner && (
        <Card
          elevation={0}
          sx={{
            mt: { xs: 2, md: 3 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardHeader
            title={
              <Typography variant="h6" fontWeight={700}>
                Add Memo
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                Add internal notes related to this order.
              </Typography>
            }
          />

          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
              <TextField
                label="Memo"
                value={memoDraft}
                onChange={(e) => setMemoDraft(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
              />

              <Box display="flex" justifyContent={{ xs: 'flex-end', md: 'flex-start' }}>
                <Button variant="contained" onClick={handleAddMemo} disabled={!memoDraft.trim()}>
                  ADD
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OrderDetail;
